import type { OwnedAccount } from '@hotjar/account';
import { mockSiteAccountDto } from '@hotjar/account';
import { mapOrganizationDtoToModel, mockOrganizationDto } from '@hotjar/organization';

import type { Site } from './dto';
import { mapSiteDtoToModel } from './mapper';
import type { Site as SiteModel } from './model';

export function mockSiteDto(overrides: Partial<Site> = {}): Site {
  return {
    account: mockSiteAccountDto(overrides.account),
    always_on_rate: 0,
    always_on_rate_change_date: null,
    always_on_rate_interim_state: null,
    anonymize_digits: true,
    anonymize_emails: true,
    continuous_capture_enabled: false,
    created: 1618365278,
    effective_plan: 'basic',
    effective_plan_for_feedback: 'basic',
    effective_plan_id: 'basic_2000',
    features: [],
    first_data: null,
    id: 14,
    industry: null,
    interim_always_on_rate: null,
    interim_plan_id: null,
    interim_plan_state: null,
    interim_sample_rate: null,
    is_in_plan_change_period: false,
    last_data: 1618401953,
    legal_name: null,
    lock_plan: false,
    monetary_discount: 0,
    name: 'Hotjar Fake',
    organization_id: 1,
    organization: mockOrganizationDto(overrides.organization),
    percentage_discount: 0,
    plan: 'basic',
    plan_change_date: null,
    plan_id: 'basic_2000',
    privacy_policy_url: null,
    recording_capture_keystrokes: true,
    sample_rate: 2000,
    sample_rate_change_date: null,
    sample_rate_interim_state: null,
    state_change_listen_mode: 'automatic',
    suppress_all: false,
    suppress_location: false,
    suppress_text: false,
    trial: {
      exists: false,
      active: false,
      days_remaining: null,
      sample_rate: null,
      always_on_rate: null,
      plan: null,
    },
    url: 'https://local.hotjar.com/demosite/',
    user_attributes_enabled: false,
    enforced_mfa_at: 1618365278,
    ...overrides,
  };
}

export function mockSiteModel(overrides: Partial<Site> = {}): SiteModel {
  const site = mockSiteDto(overrides);
  return mapSiteDtoToModel(site, [mapOrganizationDtoToModel(site.organization)]);
}

/**
 * Returns whether or not a site's plan is locked. If an `ownedAccount` is passed,
 * it will also check if the account is locked. (Every site of a locked account is also locked.)
 */
export function isPlanLocked(site: SiteModel | undefined, ownedAccount?: OwnedAccount): boolean {
  if (!site) {
    return false;
  }

  if (!ownedAccount) {
    return site.lockPlan;
  }

  return site.lockPlan || ownedAccount.lock;
}

/**
 * Returns whether or not a site is undergoing a plan change period currently.
 */
export function isPlanChangePeriod(site: SiteModel | undefined): boolean {
  if (!site) {
    return false;
  }

  return site.isInPlanChangePeriod && site.interimPlanId !== null;
}

/**
 * Returns whether or not a site is undergoing a plan downgrade period currently.
 */
export function isPlanDowngradePeriod(site: SiteModel | undefined): boolean {
  if (!site) {
    return false;
  }

  return isPlanChangePeriod(site) && site.interimPlanState === 'downgrade';
}

type AdminId = Site['account']['admin_ids'][number];

/**
 * Returns true if the user is the account owner for a site's account.
 *
 * @param userId user's ID to check for.
 * @param site Site to check owner for.
 */
export function isSiteOwner(userId?: AdminId | null, site?: SiteModel): boolean {
  if (!site || !userId) {
    return false;
  }
  return site.account.adminIds.includes(userId);
}
